/* eslint-disable @typescript-eslint/no-explicit-any */
import mqtt, { MqttClient } from 'mqtt';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import api from '../api/api';
import { getLoginUserFromCookie } from '../utils/authTokens';
import { displayToast } from '../redux/toast/toastSlice';
import { IotDeviceDetail } from '../types/iot';
import { decryptWithPresharedKey } from '../utils/presharedKey';
import { swapSlotsIfNeeded } from './weighingSlots';

export type MqttStatus =
 | 'idle'
 | 'loading'
 | 'connecting'
 | 'connected'
 | 'error';

export type WeighTab = 'pirmas' | 'antras' | 'kontrolinis';

export interface LiveWeight {
 weight: number | null;
 stable: boolean;
 task?: string;
}

export interface SlotValue {
 weight: number | null;
 time: string | null;
}

export interface LockState {
 locked: boolean;
 remainingMs: number | null;
 holderId: string | null;
 hasToken: boolean;
}

const LOCK_DEFAULT_MS = 60000;
const RESPONSE_TIMEOUT_MS = 30000;
const TOPIC_KEYS = [
 'CommandTopic',
 'ResponseTopic',
 'StateTopic',
 'CloudWeighterCommandTopic',
 'CloudWeighterResponseTopic',
 'CloudWeighterStateTopic',
 'ScalesProtocolCommandTopic',
 'ScalesProtocolStateTopic',
] as const;

type TopicMap = Partial<Record<(typeof TOPIC_KEYS)[number], string>>;

const wlog = (...args: any[]) => {
 if (import.meta.env.DEV) {
  console.log('[weigh]', ...args);
 }
};

const parseWeight = (v: unknown): number | null => {
 if (v === null || v === undefined) return null;
 const n = parseFloat(String(v).replace(',', '.'));
 return Number.isNaN(n) ? null : n;
};

const parseStable = (v: unknown): boolean =>
 v === true || v === 1 || String(v).trim().toLowerCase() === 'true' || v === '1';

const parseDurationMs = (v: unknown): number => {
 if (v == null) return LOCK_DEFAULT_MS;
 if (typeof v === 'string' && v.includes(':')) {
  const [h, m, s] = v.split(':').map((x) => parseInt(x, 10) || 0);
  const ms = (h * 3600 + m * 60 + s) * 1000;
  return ms > 0 ? ms : LOCK_DEFAULT_MS;
 }
 const n = Number(v);
 if (!Number.isFinite(n) || n <= 0) return LOCK_DEFAULT_MS;
 return n < 1000 ? n * 1000 : n;
};

const pick = (obj: any, keys: string[]): string | undefined => {
 if (!obj) return undefined;
 for (let i = 0; i < keys.length; i += 1) {
  const v = obj[keys[i]];
  if (v != null && String(v).trim() !== '') return String(v).trim();
 }
 return undefined;
};

/** Full weighing controller: live weight, scale commands, lock, weigh flow. */
export const useWeighing = (scaleId?: number | null) => {
 const dispatch = useDispatch();

 const [status, setStatus] = useState<MqttStatus>('idle');
 const [scaleName, setScaleName] = useState('');
 const [error, setError] = useState<string | null>(null);
 const [live, setLive] = useState<LiveWeight>({ weight: null, stable: false });
 const [procMsg, setProcMsg] = useState('');
 const [tab, setTab] = useState<WeighTab>('pirmas');
 const [tare, setTare] = useState<SlotValue>({ weight: null, time: null });
 const [bruto, setBruto] = useState<SlotValue>({ weight: null, time: null });
 const [neto, setNeto] = useState<number | null>(null);
 const [lock, setLock] = useState<LockState>({
  locked: false,
  remainingMs: null,
  holderId: null,
  hasToken: false,
 });
 const [weighBusy, setWeighBusy] = useState(false);
 const [weighLabel, setWeighLabel] = useState('');
 // Bumped to force the connection effect to re-run (manual + auto retry).
 const [attempt, setAttempt] = useState(0);

 const clientRef = useRef<MqttClient | null>(null);
 const topicsRef = useRef<TopicMap>({});
 const bootstrapRespRef = useRef('');
 const baseRef = useRef('');
 const callerIdRef = useRef('');
 const uidRef = useRef('');
 // Human-readable lock owner (logged-in user email) sent as the lock name.
 const lockNameRef = useRef('');

 const awaitingRef = useRef(false);
 const weightSentRef = useRef(false);
 const weighTargetRef = useRef<'tare' | 'bruto'>('bruto');
 const pendingWeighAfterLockRef = useRef(false);
 const weighPendingUnlockRef = useRef(false);
 const expectingLockAcquireRef = useRef(false);
 const expectingLockExtendRef = useRef(false);
 const respTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
 const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
 const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
 const tokenRef = useRef<string | null>(null);
 const tabRef = useRef<WeighTab>('pirmas');
 const kontroNextBrutoRef = useRef(true);
 const tareRef = useRef<number | null>(null);
 const brutoRef = useRef<number | null>(null);
 // Mirror tare/bruto SlotValue.time synchronously (state is async/batched),
 // so a tare↔bruto swap can carry the correct timestamp along with each
 // weight instead of leaving times on their original slots.
 const tareTimeRef = useRef<string | null>(null);
 const brutoTimeRef = useRef<string | null>(null);

 useEffect(() => {
  tabRef.current = tab;
 }, [tab]);

 const toast = useCallback(
  (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
   dispatch(displayToast({ message, type }));
  },
  [dispatch],
 );

 const publish = useCallback((topic?: string, payload?: unknown) => {
  const client = clientRef.current;
  if (!client || !topic) {
   wlog('publish SKIP (no client/topic)', { topic, payload });
   return false;
  }
  const body = typeof payload === 'string' ? payload : JSON.stringify(payload);
  wlog('OUT →', topic, body);
  client.publish(topic, body, { qos: 1 });
  return true;
 }, []);

 const directCmdTopic = useCallback(
  () =>
   topicsRef.current.ScalesProtocolCommandTopic ||
   topicsRef.current.CommandTopic ||
   '',
  [],
 );

 const dataWithToken = useCallback((base: Record<string, any> = {}) => {
  const out = { ...base };
  if (tokenRef.current && out.Token == null) out.Token = tokenRef.current;
  return out;
 }, []);

 // A response addressed to another web client must not drive our weigh flow.
 const isForMe = useCallback((m: any): boolean => {
  const cid = m?.callerId ?? m?.CallerId;
  if (cid == null) return true;
  return String(cid).trim() === callerIdRef.current;
 }, []);

 // ---- lock countdown ----
 const stopCountdown = useCallback(() => {
  if (countdownRef.current) {
   clearInterval(countdownRef.current);
   countdownRef.current = null;
  }
 }, []);

 const startCountdown = useCallback(
  (expiresAt: number) => {
   stopCountdown();
   const tick = () => {
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
     stopCountdown();
     tokenRef.current = null;
     try {
      if (uidRef.current) {
       localStorage.removeItem(`scales_cloudLock_${uidRef.current}`);
      }
     } catch {
      /* noop */
     }
     setLock({ locked: false, remainingMs: null, holderId: null, hasToken: false });
     return;
    }
    setLock((p) => ({ ...p, remainingMs: remaining }));
   };
   tick();
   countdownRef.current = setInterval(tick, 1000);
  },
  [stopCountdown],
 );

 const saveLockAndStart = useCallback(
  (token: string, durationMs: number) => {
   tokenRef.current = token;
   const expiresAt = Date.now() + durationMs;
   try {
    if (uidRef.current) {
     localStorage.setItem(
      `scales_cloudLock_${uidRef.current}`,
      JSON.stringify({ token, expiresAt }),
     );
    }
   } catch {
    /* noop */
   }
   setLock({
    locked: true,
    remainingMs: durationMs,
    holderId: lockNameRef.current || callerIdRef.current,
    hasToken: true,
   });
   startCountdown(expiresAt);
  },
  [startCountdown],
 );

 // ---- weigh flow ----
 const clearRespTimeout = useCallback(() => {
  if (respTimeoutRef.current) {
   clearTimeout(respTimeoutRef.current);
   respTimeoutRef.current = null;
  }
 }, []);

 const endWeighFlow = useCallback(() => {
  awaitingRef.current = false;
  weightSentRef.current = false;
  clearRespTimeout();
  setWeighBusy(false);
  setWeighLabel('');
  if (weighPendingUnlockRef.current && tokenRef.current) {
   weighPendingUnlockRef.current = false;
   publish(topicsRef.current.CommandTopic, {
    type: 'UnLock',
    callerId: callerIdRef.current,
    data: dataWithToken({}),
   });
  }
 }, [clearRespTimeout, dataWithToken, publish]);

 const startRespTimeout = useCallback(() => {
  clearRespTimeout();
  respTimeoutRef.current = setTimeout(() => {
   if (!awaitingRef.current) return;
   endWeighFlow();
   toast(
    'Per ilgai laukiama atsakymo. Patikrinkite ryšį su svarstyklėmis.',
    'warning',
   );
  }, RESPONSE_TIMEOUT_MS);
 }, [clearRespTimeout, endWeighFlow, toast]);

 const applyWeight = useCallback((w: number) => {
  const now = new Date().toISOString();
  let nextTare: SlotValue =
   weighTargetRef.current === 'tare'
    ? { weight: w, time: now }
    : { weight: tareRef.current, time: tareTimeRef.current };
  let nextBruto: SlotValue =
   weighTargetRef.current === 'bruto'
    ? { weight: w, time: now }
    : { weight: brutoRef.current, time: brutoTimeRef.current };
  ({ tare: nextTare, bruto: nextBruto } = swapSlotsIfNeeded(
   nextTare,
   nextBruto,
  ));
  tareRef.current = nextTare.weight;
  brutoRef.current = nextBruto.weight;
  tareTimeRef.current = nextTare.time;
  brutoTimeRef.current = nextBruto.time;
  setTare(nextTare);
  setBruto(nextBruto);
  setNeto(
   nextTare.weight != null && nextBruto.weight != null
    ? nextBruto.weight - nextTare.weight
    : null,
  );
  // Advance kontrolinis slot only after a weight successfully lands.
  if (tabRef.current === 'kontrolinis') {
   kontroNextBrutoRef.current = !kontroNextBrutoRef.current;
  }
 }, []);

 const sendWeighingStart = useCallback(() => {
  weighPendingUnlockRef.current = true;
  const target = weighTargetRef.current;
  const direction = target === 'bruto' ? 1 : 0;
  setWeighLabel('Laukiama svarstyklių atsakymo…');
  publish(topicsRef.current.CommandTopic, {
   type: 'WeighingStart',
   callerId: callerIdRef.current,
   data: dataWithToken({ direction, msg: 'Pradedamas svėrimas' }),
  });
  startRespTimeout();
 }, [dataWithToken, publish, startRespTimeout]);

 const weigh = useCallback(() => {
  wlog('weigh() click', {
   status,
   liveWeight: live.weight,
   tab: tabRef.current,
   hasToken: !!tokenRef.current,
  });
  if (awaitingRef.current) {
   toast('Palaukite – laukiama atsakymo', 'warning');
   return;
  }
  if (status !== 'connected') {
   toast('Nėra MQTT ryšio', 'warning');
   return;
  }
  if (live.weight == null || live.weight === 0) return;

  // compute target slot
  const t = tabRef.current;
  let target: 'tare' | 'bruto';
  if (t === 'pirmas') target = 'bruto';
  else if (t === 'antras') target = brutoRef.current == null ? 'bruto' : 'tare';
  // kontrolinis: alternate bruto/tare; toggle only after a weight lands (applyWeight).
  else target = kontroNextBrutoRef.current ? 'bruto' : 'tare';
  weighTargetRef.current = target;

  awaitingRef.current = true;
  weightSentRef.current = false;
  setWeighBusy(true);

  if (tokenRef.current) {
   sendWeighingStart();
  } else {
   pendingWeighAfterLockRef.current = true;
   expectingLockAcquireRef.current = true;
   setWeighLabel('Gaunamas užraktas…');
   publish(topicsRef.current.CommandTopic, {
    type: 'Lock',
    callerId: callerIdRef.current,
    data: { name: lockNameRef.current },
   });
   startRespTimeout();
  }
 }, [live.weight, publish, sendWeighingStart, startRespTimeout, status, toast]);

 const toggleLock = useCallback(() => {
  if (status !== 'connected') {
   toast('Nėra MQTT ryšio', 'warning');
   return;
  }
  if (lock.locked) {
   // Allow unlock even without a local token (lock may originate from CloudProc).
   // Response-driven: UI clears on the UnLock/done reply (or shows reason on error).
   publish(topicsRef.current.CommandTopic, {
    type: 'UnLock',
    callerId: callerIdRef.current,
    data: dataWithToken({}),
   });
  } else {
   expectingLockAcquireRef.current = true;
   publish(topicsRef.current.CommandTopic, {
    type: 'Lock',
    callerId: callerIdRef.current,
    data: { name: lockNameRef.current },
   });
  }
 }, [dataWithToken, lock.locked, publish, status, toast]);

 const extendLock = useCallback(() => {
  if (status !== 'connected' || !tokenRef.current) return;
  expectingLockExtendRef.current = true;
  publish(topicsRef.current.CommandTopic, {
   type: 'Lock',
   callerId: callerIdRef.current,
   data: dataWithToken({}),
  });
 }, [dataWithToken, publish, status]);

 const sendZero = useCallback(() => publish(directCmdTopic(), 'zero'), [directCmdTopic, publish]);
 const sendTare = useCallback(() => publish(directCmdTopic(), 'tare'), [directCmdTopic, publish]);
 const sendClear = useCallback(() => publish(directCmdTopic(), 'clear'), [directCmdTopic, publish]);

 const clearWeights = useCallback(() => {
  tareRef.current = null;
  brutoRef.current = null;
  tareTimeRef.current = null;
  brutoTimeRef.current = null;
  setTare({ weight: null, time: null });
  setBruto({ weight: null, time: null });
  setNeto(null);
 }, []);

 // Seed tare/bruto from an external source (e.g. /results/v2 car prefill).
 const setWeights = useCallback(
  (data: {
   tareWeight?: number | null;
   tareTime?: string | null;
   brutoWeight?: number | null;
   brutoTime?: string | null;
  }) => {
   if (data.tareWeight != null) {
    tareRef.current = data.tareWeight;
    tareTimeRef.current = data.tareTime ?? null;
    setTare({ weight: data.tareWeight, time: data.tareTime ?? null });
   }
   if (data.brutoWeight != null) {
    brutoRef.current = data.brutoWeight;
    brutoTimeRef.current = data.brutoTime ?? null;
    setBruto({ weight: data.brutoWeight, time: data.brutoTime ?? null });
   }
   setNeto(
    tareRef.current != null && brutoRef.current != null
     ? brutoRef.current - tareRef.current
     : null,
   );
  },
  [],
 );

 // Apply user-typed weights from the manual weighing dialog.
 const setManualWeights = useCallback(
  (data: { tare: number | null; bruto: number | null }) => {
   const now = new Date().toISOString();
   // Compute the next slot (weight + time together) locally rather than via
   // tareRef/brutoRef + a functional setState update: the swap helper below
   // must move a slot's timestamp along with its weight, and refs/state
   // can't be read mid-update for that.
   let nextTare: SlotValue =
    data.tare !== tareRef.current
     ? { weight: data.tare, time: data.tare == null ? null : now }
     : { weight: tareRef.current, time: tareTimeRef.current };
   let nextBruto: SlotValue =
    data.bruto !== brutoRef.current
     ? { weight: data.bruto, time: data.bruto == null ? null : now }
     : { weight: brutoRef.current, time: brutoTimeRef.current };
   ({ tare: nextTare, bruto: nextBruto } = swapSlotsIfNeeded(
    nextTare,
    nextBruto,
   ));

   tareRef.current = nextTare.weight;
   brutoRef.current = nextBruto.weight;
   tareTimeRef.current = nextTare.time;
   brutoTimeRef.current = nextBruto.time;
   setTare(nextTare);
   setBruto(nextBruto);
   setNeto(
    nextTare.weight != null && nextBruto.weight != null
     ? nextBruto.weight - nextTare.weight
     : null,
   );
  },
  [],
 );

 // Force a fresh bootstrap + broker connection.
 const reconnect = useCallback(() => {
  if (retryTimerRef.current) {
   clearTimeout(retryTimerRef.current);
   retryTimerRef.current = null;
  }
  setAttempt((a) => a + 1);
 }, []);

 // ---- lock response handling ----
 const handleLock = useCallback(
  (msg: any) => {
   const type = String(msg.type || '').toLowerCase();
   const state = String(msg.state || '').toLowerCase();
   const isError = state === 'error' || !!msg.Error || !!msg.error;
   const reason = () =>
    String(
     msg.msg ?? msg.message ?? msg.result?.Error ?? msg.data?.msg ?? 'Klaida',
    );

   // ---- UnLock response (response-driven; not optimistic) ----
   if (type === 'unlock') {
    if (isError) {
     // Server refused unlock → keep lock state, surface reason.
     toast(reason(), 'error');
     return;
    }
    expectingLockAcquireRef.current = false;
    expectingLockExtendRef.current = false;
    pendingWeighAfterLockRef.current = false;
    stopCountdown();
    tokenRef.current = null;
    try {
     if (uidRef.current) localStorage.removeItem(`scales_cloudLock_${uidRef.current}`);
    } catch {
     /* noop */
    }
    setLock({ locked: false, remainingMs: null, holderId: null, hasToken: false });
    toast('Atrakinta', 'success');
    return;
   }

   // ---- Lock acquire / extend ----
   if (isError) {
    const wasExtend = expectingLockExtendRef.current;
    expectingLockAcquireRef.current = false;
    expectingLockExtendRef.current = false;
    toast(reason() !== 'Klaida'
     ? reason()
     : wasExtend
      ? 'Nepavyko pratęsti užrakto'
      : 'Nepavyko užrakti', 'error');
    if (pendingWeighAfterLockRef.current) {
     pendingWeighAfterLockRef.current = false;
     endWeighFlow();
    }
    return;
   }

   if (state === 'done') {
    const respToken = pick(msg.result, ['Token']) ?? pick(msg.data, ['Token']);
    const durFound =
     msg.duration != null ||
     msg.result?.duration != null ||
     msg.data?.duration != null;
    const durationMs = parseDurationMs(
     msg.duration ?? msg.result?.duration ?? msg.data?.duration,
    );
    const hadToken = !!tokenRef.current;
    // Extend acks may carry only a (new) duration, no token → reuse stored token.
    const token = respToken ?? (hadToken && durFound ? tokenRef.current : null);
    if (token) {
     const extending =
      expectingLockExtendRef.current ||
      (hadToken && (token === tokenRef.current || (!respToken && durFound)));
     expectingLockAcquireRef.current = false;
     expectingLockExtendRef.current = false;
     saveLockAndStart(token, durationMs);
     toast(extending ? 'Užraktas pratęstas' : 'Užraktas aktyvus', 'success');
     if (pendingWeighAfterLockRef.current) {
      pendingWeighAfterLockRef.current = false;
      sendWeighingStart();
     }
    }
   }
  },
  [endWeighFlow, saveLockAndStart, sendWeighingStart, stopCountdown, toast],
 );

 useEffect(() => {
  if (!scaleId) {
   setStatus('idle');
   return undefined;
  }

  let cancelled = false;
  let client: MqttClient | null = null;
  if (retryTimerRef.current) {
   clearTimeout(retryTimerRef.current);
   retryTimerRef.current = null;
  }
  const subscribed = new Set<string>();
  const subscribe = (t?: string | null) => {
   if (!t || subscribed.has(t)) return;
   subscribed.add(t);
   client?.subscribe(t, { qos: 1 });
  };

  const run = async () => {
   try {
    setStatus('loading');
    setError(null);
    setLive({ weight: null, stable: false });

    const { data: pskResp } = await api.get('/iot/v1/presharedkey');
    if (cancelled) return;
    const presharedKey: string = pskResp?.Key;
    if (!presharedKey) {
     setStatus('error');
     setError('Missing preshared key');
     return;
    }

    const { data } = await api.get(`/iot/v1/${scaleId}`);
    if (cancelled) return;
    const detail = data as IotDeviceDetail;
    setScaleName(detail?.Name ?? '');

    const src = detail?.IotSource;
    wlog('iot detail', {
     scaleId,
     Name: detail?.Name,
     Path: detail?.Path,
     hasSource: !!src,
     ServerIP: src?.ServerIP,
     ServerPort: src?.ServerPort,
     hasUser: !!src?.User?.Name,
    });
    if (!src?.ServerIP || !src?.ServerPort) {
     wlog('ABORT: missing broker config', src);
     setStatus('error');
     setError('Missing broker config (ServerIP/ServerPort)');
     return;
    }
    if (!src.User?.Name || !src.User?.Password) {
     wlog('ABORT: missing broker credentials');
     setStatus('error');
     setError('Missing broker credentials');
     return;
    }

    const [username, password] = await Promise.all([
     decryptWithPresharedKey(presharedKey, src.User.Name),
     decryptWithPresharedKey(presharedKey, src.User.Password),
    ]);
    if (cancelled) return;

    const proto = src.ServerPort === 15675 ? 'ws' : 'wss';
    const url = `${proto}://${src.ServerIP}:${src.ServerPort}/ws`;
    const base = [src.Path, src.UID, detail.Path]
     .map((p) => String(p ?? '').replace(/^\/+|\/+$/g, ''))
     .filter(Boolean)
     .join('/');
    baseRef.current = base;
    bootstrapRespRef.current = base ? `${base}/resp` : '';
    uidRef.current = src.UID || '';
    lockNameRef.current = getLoginUserFromCookie()?.email || src.UID || '';
    callerIdRef.current = `web_${scaleId}_${Math.floor(Math.random() * 1e6)}`;
    topicsRef.current = {};
    tokenRef.current = null;

    setStatus('connecting');
    client = mqtt.connect(url, {
     username,
     password,
     clientId: `se_web_${scaleId}_${Math.floor(Math.random() * 1e6)}`,
     protocolVersion: 4,
     keepalive: 30,
     connectTimeout: 30000,
     reconnectPeriod: 4000,
     clean: true,
    });
    clientRef.current = client;

    client.on('connect', () => {
     if (cancelled) return;
     setStatus('connected');
     wlog('connected', { url, base, callerId: callerIdRef.current });
     subscribe(base);
     subscribe(bootstrapRespRef.current);
     if (base) publish(`${base}/cmd`, { type: 'Config' });
    });

    client.on('message', (topic, payload) => {
     if (cancelled) return;
     let msg: any;
     try {
      msg = JSON.parse(payload.toString());
     } catch {
      return;
     }
     if (!msg || typeof msg !== 'object') return;

     const tm = topicsRef.current;
     const isProcResp =
      topic === tm.ResponseTopic || topic === bootstrapRespRef.current;
     const isWeighterResp = topic === tm.CloudWeighterResponseTopic;
     const isProcState = topic === tm.StateTopic;
     const isScalesState =
      topic === tm.ScalesProtocolStateTopic || topic === baseRef.current;

     if (!isScalesState) {
      // Skip noisy continuous weight stream; log everything else.
      wlog('IN ←', topic, {
       type: msg.type,
       state: msg.state,
       match: { isProcResp, isWeighterResp, isProcState },
       awaiting: awaitingRef.current,
      });
     }

     // Config reply → topic map + subscribe live topics + ask State
     if (
      isProcResp &&
      msg.type === 'Config' &&
      msg.state === 'done' &&
      msg.result
     ) {
      const r = msg.result;
      const map: TopicMap = {};
      TOPIC_KEYS.forEach((k) => {
       const cands = [k, k[0].toLowerCase() + k.slice(1)];
       // Broker emits the "Weigher" (no 't') typo variant for CloudWeighter* keys.
       if (k.includes('Weighter')) {
        const alt = k.replace('Weighter', 'Weigher');
        cands.push(alt, alt[0].toLowerCase() + alt.slice(1));
       }
       const v = pick(r, cands);
       if (v) map[k] = v;
      });
      topicsRef.current = map;
      subscribe(map.ResponseTopic);
      subscribe(map.StateTopic);
      subscribe(map.CloudWeighterResponseTopic);
      subscribe(map.CloudWeighterStateTopic);
      subscribe(map.ScalesProtocolStateTopic);
      publish(map.CommandTopic, {
       type: 'State',
       callerId: callerIdRef.current,
      });
      return;
     }

     if (isProcResp) {
      const type = String(msg.type || '');
      // Lock/UnLock routed for any caller (updates holder + our token/state).
      if (/^(un)?lock$/i.test(type)) {
       handleLock(msg);
       return;
      }
      // Weigh-flow replies addressed to other web clients are not ours to act on.
      if (!isForMe(msg)) return;
      // Any error state on the proc response → surface the reason.
      if (String(msg.state || '').toLowerCase() === 'error') {
       const m =
        msg.msg ?? msg.message ?? (msg.data && msg.data.msg) ?? 'Klaida';
       if (awaitingRef.current) endWeighFlow();
       toast(String(m), 'error');
       return;
      }
      if (type === 'WeighingStart' && msg.state === 'done') {
       if (!awaitingRef.current || weightSentRef.current) return;
       weightSentRef.current = true;
       setWeighLabel('Gaunamas svoris…');
       const wData = dataWithToken({});
       publish(
        tm.CloudWeighterCommandTopic,
        wData.Token
         ? { type: 'Weight', callerId: callerIdRef.current, data: wData }
         : { type: 'Weight', callerId: callerIdRef.current },
       );
       startRespTimeout();
       return;
      }
      if (type === 'WeighingStart' && msg.state === 'error') {
       if (!awaitingRef.current) return;
       endWeighFlow();
       toast(String(msg.msg || msg.message || 'Klaida'), 'error');
       return;
      }
      if (type === 'WeighingDone') {
       if (!awaitingRef.current) return;
       endWeighFlow();
       toast(
        msg.state === 'error'
         ? String(msg.msg || msg.message || 'Klaida')
         : 'Baigtas svėrimas',
        msg.state === 'error' ? 'error' : 'success',
       );
       return;
      }
      return;
     }

     if (isWeighterResp && msg.type === 'Weight') {
      if (!awaitingRef.current || !isForMe(msg)) return;
      if (msg.state === 'done') {
       const w = parseWeight(msg.weight) ?? 0;
       applyWeight(w);
       endWeighFlow();
       toast('Svoris gautas', 'success');
      } else if (msg.state === 'error') {
       endWeighFlow();
       toast(String(msg.msg || msg.message || 'Klaida'), 'error');
      }
      return;
     }

     if (isProcState) {
      // CloudProc state: { Lock:{Locker:{Name},IsLocked}, msg, full, sensors }
      if (msg.msg !== undefined) setProcMsg(String(msg.msg ?? ''));
      const rawLocked = msg?.Lock?.IsLocked;
      const isLocked =
       rawLocked === true || String(rawLocked).toLowerCase() === 'true';
      if (isLocked) {
       // Locker.Name carries the human owner (logged-in email) we sent on Lock.
       const lockerName = pick(msg?.Lock?.Locker, ['Name']);
       setLock((p) => ({
        ...p,
        locked: true,
        holderId: lockerName ?? p.holderId,
       }));
      } else if (
       rawLocked === false ||
       String(rawLocked).toLowerCase() === 'false'
      ) {
       stopCountdown();
       tokenRef.current = null;
       setLock({ locked: false, remainingMs: null, holderId: null, hasToken: false });
      }
      return;
     }

     if (isScalesState) {
      const wRaw = msg.weight ?? msg.Weight;
      if (wRaw !== undefined || msg.stable !== undefined || msg.task !== undefined) {
       setLive({
        weight: parseWeight(wRaw),
        stable: parseStable(msg.stable ?? msg.Stable),
        task: msg.task ?? msg.Task,
       });
      }
     }
    });

    client.on('error', (e: Error) => {
     if (cancelled) return;
     setStatus('error');
     setError(e.message);
    });
    client.on('close', () => {
     if (cancelled) return;
     setStatus((s) => (s === 'connected' ? 'connecting' : s));
    });
   } catch (e: any) {
    if (cancelled) return;
    setStatus('error');
    setError(e?.message ?? 'Failed to load scale config');
    // Bootstrap (psk/iot config) can fail at page load before auth is hydrated.
    // The effect deps don't change, so auto-retry to recover the connection.
    if (!retryTimerRef.current) {
     retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      if (!cancelled) setAttempt((a) => a + 1);
     }, 5000);
    }
   }
  };

  run();

  return () => {
   cancelled = true;
   stopCountdown();
   clearRespTimeout();
   if (retryTimerRef.current) {
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
   }
   try {
    // Detach handlers first: end(true) closes the socket but messages already
    // queued on it can still re-enter 'message' and flood the console feed.
    client?.removeAllListeners();
    client?.end(true);
   } catch {
    /* noop */
   }
   clientRef.current = null;
   topicsRef.current = {};
   tokenRef.current = null;
   awaitingRef.current = false;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [scaleId, attempt]);

 return {
  status,
  scaleName,
  error,
  live,
  procMsg,
  tab,
  setTab,
  tare,
  bruto,
  neto,
  lock,
  weighBusy,
  weighLabel,
  weigh,
  toggleLock,
  extendLock,
  sendZero,
  sendTare,
  sendClear,
  clearWeights,
  setWeights,
  setManualWeights,
  reconnect,
 };
};

export default useWeighing;
